import React from "react";
import { WizardStepConfig } from "./wizardHelpers";
import ReferralCreditSummary from "../../Output/ReferralCreditSummary";
import PhaseProgressTimeline from "../../Output/PhaseProgressTimeline";
import type { PhaseStatus } from "../../Output/PhaseProgressTimeline";

const MS = {
	EIGHT_DAYS: 1000 * 60 * 60 * 24 * 8
};

/**
 * Retention wizard step definitions for the cancel subscription flow.
 *
 * Each step is rendered by CancelWizard via WizardStepRenderer. The
 * `headline` and `body` fields are Handlebars templates compiled against
 * the `wizardData` object built in Account.tsx → handleCancel(). Markdown
 * in the compiled output is converted to HTML by `marked`.
 *
 * Template syntax:
 *   {{helper value}}         — escaped output (currency, number, pluralize, monthYear)
 *   {{{helper value}}}       — raw/unescaped HTML output (featureCardFormatter)
 *   **text**                 — Markdown bold, rendered as <strong>
 *
 * Conditional steps:
 *   condition: (data) => boolean  — function evaluated against wizardData
 *   (omitted)                     — step is always shown
 *
 * Available data keys (see Account.tsx wizardData):
 *   currentPrice, newPrice, memberSince, planName, jobCount,
 *   featureCount, features (FeatureItem[]), teamMemberCount, team.hasMembers
 */
export const cancellationSteps: WizardStepConfig[] = [
	// Step 1 — Grandfathered pricing (only shown when newPrice > currentPrice)
	{
		headline: "You're locked in at {{currency currentPrice}}/mo",
		body:
			"Your current plan is grandfathered at **{{currency currentPrice}}/mo** — " +
			"New subscribers pay **{{currency newPrice}}/mo** for the same features.\n\n" +
			"If you cancel now, you'll lose this rate forever.",
		detail:
			"You've been a member since **{{monthYear memberSince}}** — " +
			"that's loyalty we value.",
		condition: (data) => Number(data.newPrice) > Number(data.currentPrice)
	},
	// Step 2 — Brand new account (only shown when subscription start is <= 7 days ago)
	{
		headline: "You've only just arrived!",
		body:
			"You signed up just **{{daysAgo memberSince}}** &mdash; you've barely had a chance to scratch the surface.\n\n" +
			"Your subscription is paid through to the end of the month, so you won't be charged again &mdash; " +
			"but if you cancel now, access cuts off the moment your billing period ends.",
		detail:
			"Give it a little longer. Most users hit their stride in the first few weeks!",
		condition: (data) =>
			typeof data.memberSince === "string" &&
			Date.now() - Date.parse(data.memberSince) < MS.EIGHT_DAYS
	},
	// Step 3 — Feature usage recap (only shown when at least 1 feature has been used 10 or more times)
	{
		headline: "Here is what you are using ",
		body:
			"Since joining, you've created **{{number jobCount}}** jobs with your **{{planName}}** plan. Here are your most-used features:\n\n" +
			"{{{featureCardFormatter features}}}",
		condition: (data) => {
			return (
				Array.isArray(data.features) &&
				data.features.find((f) => Number(f.usageCount) >= 10) !== undefined
			);
		}
	},
	// Step 4 — Team impact (only shown when the user has team members)
	{
		headline: "Your team is counting on you",
		body:
			"You have **{{number teamMemberCount}}** {{pluralize teamMemberCount 'team member'}} " +
			"who rely on your subscription.\n\n" +
			"Cancelling will remove their access too.",
		condition: (data) => {
			return (
				typeof data.team === "object" &&
				data.team !== null &&
				Boolean((data.team as any).hasMembers)
			);
		}
	},
	// Step 5 — Referrer warning (only shown when the user was referred and has pending credits)
	{
		headline: "{{referrerName}} is counting on you",
		body:
			"**{{referrerName}}** referred you to JobMap and earns bonuses when you stay " +
			"subscribed. If you cancel now, they'll miss out on **{{number referrerPendingCredits}} credits** " +
			"they haven't received yet.",
		condition: (data) => Number(data.referrerPendingCredits) > 0,
		component: (data: Record<string, unknown>) => {
			const awarded = Number(data.referrerPhasesAwarded ?? 0);
			const total = 3;
			const pending = total - awarded;
			const phases: PhaseStatus[] = Array.from({ length: total }, (_, i) => ({
				phase: (i + 1) as 1 | 2 | 3,
				status: i < awarded ? ("awarded" as const) : ("pending" as const)
			}));
			return React.createElement(
				"div",
				{
					className:
						"bg-primary-accent border border-primary-inactive rounded-xl p-4 mt-4"
				},
				React.createElement(
					"div",
					{ className: "flex justify-between items-center mb-3" },
					React.createElement(
						"span",
						{ className: "text-primary-active font-bold text-sm" },
						"Referral Bonus Progress"
					),
					React.createElement(
						"span",
						{ className: "text-primary-scrim text-xs" },
						`${awarded} of ${total} awarded`
					)
				),
				React.createElement(PhaseProgressTimeline, { phases }),
				React.createElement(
					"p",
					{
						className: "text-primary-scrim text-xs mt-3"
					},
					`${pending} remaining ${pending === 1 ? "bonus" : "bonuses"} will be `,
					React.createElement(
						"span",
						{ className: "font-bold", style: { color: "#f53500" } },
						"forfeited"
					),
					" if you cancel."
				)
			);
		}
	},
	// Step 6 — Originator warning (only shown when the user has earned referral credits)
	{
		headline: "You'll lose your referral credits",
		body:
			"You've built up **{{number originatorTotalCredits}}** credits by referring friends to JobMap. " +
			"If you cancel, every credit — earned and pending — will be permanently forfeited.",
		condition: (data) =>
			Number(data.originatorCreditsEarned) > 0 ||
			Number(data.originatorCreditsPending) > 0,
		component: (data: Record<string, unknown>) =>
			React.createElement(ReferralCreditSummary, {
				creditsEarned: Number(data.originatorCreditsEarned ?? 0),
				creditsPending: Number(data.originatorCreditsPending ?? 0),
				referralNames: (data.originatorReferralNames as string[]) ?? []
			})
	}
];

/**
 * A default cancellation step to be used if no other steps' conditions are met
 */
export const defaultCancellationStep: WizardStepConfig = {
	headline: "Are you sure?",
	body:
		"Your subscription is paid through to the end of the month, so you won't be charged again &mdash; " +
		"but if you cancel now, access cuts off the moment your billing period ends."
};
